import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Alert, Form, Button, Dropdown, Spinner } from 'react-bootstrap';
import SkeletonLoader, { SeatMapSkeleton, EventHeaderSkeleton } from '../components/common/SkeletonLoader';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import InternationalPhoneInput from '../components/common/InternationalPhoneInput';
import SeatMap from '../components/event/seated/SeatMap';
import './SeatedRegistrationPage.scss';

interface Seat {
  id: number;
  row: string;
  column: number;
  seat_number: string;
  status: 'available' | 'selected' | 'reserved' | 'sold' | 'held';
  is_available: boolean;
  sold: boolean;
  disabled: boolean;
  held_until?: string;
  held_by?: string;
  in_cart: boolean;
}

interface EventData {
  id: number;
  title: string;
  slug: string;
  setting: {
    live: boolean;
    seated: boolean;
    collect_individual_information: boolean;
    registration_limit: number;
    quota_limit: number;
    currency: {
      symbol: string;
    };
  };
  categories?: CategoryData[];
  organized_categories?: {
    clustered?: Record<string, {
      id: number;
      name: string;
      categories: CategoryData[];
    }>;
  };
}

interface CategoryData {
  id: number;
  name: string;
  price: number;
  remaining: number;
  sold_out: boolean;
  registration_type: string;
  quantity: number;
  available_seats: number;
}

interface AttendeeForm {
  name: string;
  email: string;
  phone: string;
  custom_fields: Record<string, any>;
}

const SeatedRegistrationPage: React.FC = () => {
  const { slug, categoryId: urlCategoryId } = useParams<{ slug: string; categoryId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Support both URL patterns: /seated/:categoryId and /seated?category=id
  const categoryId = urlCategoryId ? parseInt(urlCategoryId) : parseInt(searchParams.get('category') || '0');
  
  // State management
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [categoryData, setCategoryData] = useState<CategoryData | null>(null);
  const [availableCategories, setAvailableCategories] = useState<CategoryData[]>([]);
  const [categoryClusters, setCategoryClusters] = useState<Array<{
    clusterId: string;
    clusterName: string;
    categories: CategoryData[];
  }>>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [selectedSeats, setSelectedSeats] = useState<Set<number>>(new Set());
  const [pendingSeats, setPendingSeats] = useState<Set<number>>(new Set());
  const [quotaSeatId, setQuotaSeatId] = useState<number | null>(null);
  const [conflictSeatId, setConflictSeatId] = useState<number | null>(null);
  const [attendees, setAttendees] = useState<AttendeeForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    if (!slug || !categoryId) {
      setError('Invalid event or category');
      setLoading(false);
      return;
    }

    loadInitialData();
  }, [slug, categoryId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load event data
      const event = await apiService.events.getEventDetail(slug!);
      setEventData(event);

      // Process organized categories if available, otherwise use flat categories
      let seatedCategories: CategoryData[] = [];
      let clusters: Array<{
        clusterId: string;
        clusterName: string;
        categories: CategoryData[];
      }> = [];

      if (event.organized_categories?.clustered) {
        // Use organized categories with clusters
        Object.entries(event.organized_categories.clustered).forEach(([clusterId, cluster]: [string, any]) => {
          const seatedCats = cluster.categories.filter((cat: CategoryData) => 
            cat.registration_type === 'seated' && !cat.sold_out
          );
          if (seatedCats.length > 0) {
            clusters.push({
              clusterId,
              clusterName: cluster.name,
              categories: seatedCats
            });
            seatedCategories.push(...seatedCats);
          }
        });
      } else {
        // Fallback to flat categories
        seatedCategories = event.categories?.filter((cat: CategoryData) => 
          cat.registration_type === 'seated' && !cat.sold_out
        ) || [];
        if (seatedCategories.length > 0) {
          clusters.push({
            clusterId: 'default',
            clusterName: 'Categories',
            categories: seatedCategories
          });
        }
      }

      setAvailableCategories(seatedCategories);
      setCategoryClusters(clusters);

      // Find category data
      const category = seatedCategories.find((cat: CategoryData) => cat.id === categoryId);
      if (!category) {
        if (seatedCategories.length > 0) {
          // Redirect to first available category if current one not found
          navigate(`/events/${slug}/seated/${seatedCategories[0].id}`, { replace: true });
          return;
        } else {
          setError('No seated categories available');
          return;
        }
      }
      setCategoryData(category);

      // Load seat map data
      await loadSeatMapData();
    } catch (err: any) {
      console.error('Error loading initial data:', err);
      setError('Failed to load event data');
    } finally {
      setLoading(false);
    }
  };

  const loadSeatMapData = async () => {
    try {
      const data = await apiService.events.getSeatMapData(slug!, categoryId);
      
      if (data.success) {
        setSeats(data.seats);
        setSessionId(data.session_id);
        
        // Add seats held by current session to selected seats
        const heldSeats = new Set<number>();
        data.seats.forEach((seat: Seat) => {
          if (seat.held_by === data.session_id && seat.held_until && new Date(seat.held_until) > new Date()) {
            heldSeats.add(seat.id);
          }
        });
        setSelectedSeats(heldSeats);
      } else {
        setError(data.error || 'Failed to load seat map');
      }
    } catch (err: any) {
      console.error('Error loading seat map:', err);
      setError('Failed to load seat map');
    }
  };


  const handleRefreshSeats = useCallback(async () => {
    if (!slug || !categoryId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.events.getSeatMapData(slug, categoryId);
      if (result.success) {
        setSeats(result.seats);
        setSessionId(result.session_id);
      } else {
        setError(result.error || 'Failed to refresh seats');
      }
    } catch (err: any) {
      console.error('Error refreshing seats:', err);
      setError(err.message || 'Failed to refresh seats');
    } finally {
      setLoading(false);
    }
  }, [slug, categoryId]);

  // Check registration limits
  const canSelectMoreSeats = useCallback(() => {
    if (!eventData || !categoryData) return false;
    
    const currentSelection = selectedSeats.size;
    const maxSeats = Math.min(
      eventData.setting.registration_limit || 10,
      eventData.setting.quota_limit || 10,
      categoryData.available_seats || 10
    );
    
    return currentSelection < maxSeats;
  }, [eventData, categoryData, selectedSeats.size]);


  // Handle category change
  const handleCategoryChange = (newCategoryId: number) => {
    navigate(`/events/${slug}/seated/${newCategoryId}`);
  };

  // Handle back to event page
  const handleBackToEvent = () => {
    navigate(`/events/${slug}`);
  };

  // Get cluster name for current category
  const getCurrentClusterName = (): string => {
    if (!categoryData) return '';
    
    const cluster = categoryClusters.find(cluster => 
      cluster.categories.some(cat => cat.id === categoryData.id)
    );
    return cluster?.clusterName || '';
  };

  const handleSeatToggle = useCallback(async (seatId: number) => {
    if (selectedSeats.has(seatId)) {
      // Release seat
      try {
        setPendingSeats(prev => new Set([...Array.from(prev), seatId]));
        setError(null); // Clear any previous errors
        
        // Call API to release seat
        const result = await apiService.events.releaseSeats([seatId]);
        
        if (result.success) {
          // Update selected seats state
          setSelectedSeats(prev => {
            const newSet = new Set(prev);
            newSet.delete(seatId);
            return newSet;
          });
          
          // Update local seat state to mark as available
          setSeats(prev => prev.map(s => 
            s.id === seatId
              ? {
                  ...s,
                  status: 'available',
                  held_by: undefined,
                  held_until: undefined,
                  in_cart: false,
                  sold: false,
                  disabled: false,
                  is_available: true,
                }
              : s
          ));
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`Seat ${seatId} released successfully:`, result);
          }
        } else {
          // Even if API says it failed, update local state to prevent UI issues
          console.warn(`Seat release API returned success=false for seat ${seatId}:`, result);
          setSelectedSeats(prev => {
            const newSet = new Set(prev);
            newSet.delete(seatId);
            return newSet;
          });
          setSeats(prev => prev.map(s => 
            s.id === seatId
              ? {
                  ...s,
                  status: 'available',
                  held_by: undefined,
                  held_until: undefined,
                  in_cart: false,
                  sold: false,
                  disabled: false,
                  is_available: true,
                }
              : s
          ));
        }
        
      } catch (err: any) {
        console.error('Error releasing seat:', err);
        // Even on error, update local state to prevent UI issues
        // The backend will eventually release expired holds
        setSelectedSeats(prev => {
          const newSet = new Set(prev);
          newSet.delete(seatId);
          return newSet;
        });
        setSeats(prev => prev.map(s => 
          s.id === seatId
            ? {
                ...s,
                status: 'available',
                held_by: undefined,
                held_until: undefined,
                in_cart: false,
                sold: false,
                disabled: false,
                is_available: true,
              }
            : s
        ));
        // Only show error if it's not a network error (might be session issue)
        if (err.response?.status !== 400 && err.response?.status !== 404) {
          setError(err.response?.data?.error || err.message || 'Failed to release seat. It will be released automatically.');
        }
      } finally {
        setPendingSeats(prev => {
          const next = new Set(prev);
          next.delete(seatId);
          return next;
        });
      }
    } else {
      // Check if we can select more seats based on event settings
      if (!canSelectMoreSeats()) {
        const maxSeats = Math.min(
          eventData?.setting.registration_limit || 10,
          eventData?.setting.quota_limit || 10,
          categoryData?.available_seats || 10
        );
        setError(`You can only select up to ${maxSeats} seats`);
        setQuotaSeatId(seatId);
        setTimeout(() => setQuotaSeatId(null), 900);
        return;
      }

      // Check if seat is already selected by another user (double-booking prevention)
      const seat = seats.find(s => s.id === seatId);
      if (seat && (seat.sold || seat.status === 'reserved' || seat.status === 'held')) {
        setError(`Seat ${seat.seat_number} is no longer available`);
        return;
      }

      // Hold seat
      try {
        setPendingSeats(prev => new Set([...Array.from(prev), seatId]));
        setError(null); // Clear any previous errors
        
        const result = await apiService.events.holdSeats([seatId]);
        
        if (result.success) {
          // Update selected seats state
          setSelectedSeats(prev => new Set([...Array.from(prev), seatId]));
          
          // Update local seat state to mark as held
          setSeats(prev => prev.map(s => 
            s.id === seatId
              ? {
                  ...s,
                  status: 'selected',
                  held_by: result.session_id,
                  held_until: result.hold_until,
                  is_available: true,
                }
              : s
          ));
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`Seat ${seatId} held successfully:`, result);
          }
        } else {
          // API returned success=false
          setError(result.error || 'Seat is no longer available');
          setSeats(prev => prev.map(s => (
            s.id === seatId
              ? {
                  ...s,
                  status: 'reserved',
                  is_available: false,
                }
              : s
          )));
          setConflictSeatId(seatId);
          setTimeout(() => setConflictSeatId(null), 1100);
        }
      } catch (err: any) {
        console.error('Error holding seat:', err);
        console.error('Error response:', err.response?.data);
        console.error('Error status:', err.response?.status);
        
        if (err.response?.status === 422) {
          const errorMessage = err.response.data?.error || 'Seat is no longer available';
          setError(errorMessage);
          // Mark seat as reserved locally to reflect conflict
          setSeats(prev => prev.map(s => (
            s.id === seatId
              ? {
                  ...s,
                  status: 'reserved',
                  is_available: false,
                }
              : s
          )));
          setConflictSeatId(seatId);
          setTimeout(() => setConflictSeatId(null), 1100);
        } else if (err.response?.status === 404) {
          setError('Seat not found. Please refresh the page.');
        } else {
          setError(err.response?.data?.error || err.message || 'Failed to hold seat. Please try again.');
        }
      } finally {
        setPendingSeats(prev => {
          const next = new Set(prev);
          next.delete(seatId);
          return next;
        });
      }
    }
  }, [selectedSeats, canSelectMoreSeats, eventData, categoryData, seats, handleRefreshSeats]);

  // Clear all selected seats
  const handleClearAllSeats = useCallback(async () => {
    if (selectedSeats.size === 0) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get current selected seats before clearing
      const seatIds = Array.from(selectedSeats);
      
      // Release all selected seats
      await apiService.events.releaseSeats(seatIds);
      
      // Clear selected seats
      setSelectedSeats(new Set());
      
      // Update local seat state to mark all selected seats as available
      setSeats(prev => prev.map(seat => 
        seatIds.includes(seat.id)
          ? {
              ...seat,
              status: 'available',
              held_by: undefined,
              held_until: undefined,
              in_cart: false,
              sold: false,
              disabled: false,
              is_available: true,
            }
          : seat
      ));
      
      setError(null);
    } catch (err: any) {
      console.error('Error clearing seats:', err);
      setError(err.message || 'Failed to clear seat selection');
    } finally {
      setLoading(false);
    }
  }, [selectedSeats, apiService.events]);

  const handleAttendeeChange = (index: number, field: keyof AttendeeForm, value: any) => {
    setAttendees(prev => {
      const newAttendees = [...prev];
      if (!newAttendees[index]) {
        newAttendees[index] = { name: '', email: '', phone: '', custom_fields: {} };
      }
      newAttendees[index][field] = value;
      return newAttendees;
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedSeats.size === 0) {
      setError('Please select at least one seat');
      return;
    }

    // Only validate attendee information if collect_individual_information is enabled
    if (eventData?.setting.collect_individual_information) {
      if (selectedSeats.size !== attendees.length) {
        setError('Please fill in information for all selected seats');
        return;
      }

      // Validate attendee information for all attendees
      for (let i = 0; i < attendees.length; i++) {
        const attendee = attendees[i];
        if (!attendee.name || !attendee.email || !attendee.phone) {
          setError(`Please fill in all required fields for attendee ${i + 1}`);
          return;
        }
      }
    } else {
      // Only validate primary attendee (index 0) when collect_individual_information is disabled
      const primaryAttendee = attendees[0];
      if (!primaryAttendee?.name || !primaryAttendee?.email || !primaryAttendee?.phone) {
        setError('Please fill in all required fields');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      const registrationData = {
        event_slug: slug!,
        category_id: categoryId,
        seats: Array.from(selectedSeats).map((seatId, index) => {
          // If collect_individual_information is disabled, use primary attendee info for all seats
          const attendeeIndex = eventData?.setting.collect_individual_information ? index : 0;
          const attendee = attendees[attendeeIndex];
          
          return {
            seat_id: seatId,
            name: attendee.name,
            email: attendee.email,
            phone: attendee.phone,
            custom_fields: attendee.custom_fields
          };
        })
      };

      const result = await apiService.events.submitSeatedRegistration(registrationData);
      
      if (result.success) {
        // Redirect to cart page
        window.location.href = '/cart/';
      } else {
        setError(result.error || 'Registration failed');
      }
    } catch (err: any) {
      console.error('Error submitting registration:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Handle different types of errors
      if (err.response?.status === 422) {
        // Validation error - show detailed error message
        const errorData = err.response.data;
        if (errorData.error) {
          setError(errorData.error);
        } else if (errorData.errors) {
          // Field-specific errors
          const fieldErrors = Object.entries(errorData.errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          setError(`Validation error: ${fieldErrors}`);
        } else {
          setError('The selected seats are no longer available. Please refresh and select different seats.');
        }
      } else if (err.response?.status === 400) {
        const errorData = err.response.data;
        setError(errorData.error || errorData.message || 'Invalid request. Please check your form data.');
      } else {
        setError(err.response?.data?.error || err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Update attendees array when selected seats change
  useEffect(() => {
    const seatArray = Array.from(selectedSeats);
    setAttendees(prev => {
      const newAttendees: AttendeeForm[] = [];
      for (let i = 0; i < seatArray.length; i++) {
        newAttendees[i] = prev[i] || { name: '', email: '', phone: '', custom_fields: {} };
      }
      return newAttendees;
    });
  }, [selectedSeats]);

  // Cleanup: Release all held seats when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      // Release all selected seats when component unmounts
      const seatsToRelease = Array.from(selectedSeats);
      if (seatsToRelease.length > 0) {
        // Use a fire-and-forget approach to avoid blocking unmount
        apiService.events.releaseSeats(seatsToRelease).catch(err => {
          console.warn('Failed to release seats on unmount:', err);
        });
      }
    };
  }, [selectedSeats]);

  // Auto-fill form data when user is authenticated
  useEffect(() => {
    console.log('Auto-fill effect triggered:', { isAuthenticated, user: user ? 'present' : 'null', attendeesLength: attendees.length });
    
    if (isAuthenticated && user) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      const phoneNumber = user.profile?.phone_number || '';
      
      console.log('Auto-fill data:', { fullName, email: user.email, phoneNumber });
      
      setAttendees(prev => {
        // Ensure we have at least one attendee (primary attendee)
        const currentAttendees = prev.length > 0 ? prev : [{ name: '', email: '', phone: '', custom_fields: {} }];
        const primaryAttendee = currentAttendees[0];
        
        console.log('Primary attendee before auto-fill:', primaryAttendee);
        
        // Auto-fill if the primary attendee fields are empty
        if (!primaryAttendee.name || !primaryAttendee.email || !primaryAttendee.phone) {
          const updatedAttendees = currentAttendees.map((attendee, index) => {
            if (index === 0) { // Only auto-fill primary attendee
              return {
                ...attendee,
                name: attendee.name || fullName,
                email: attendee.email || user.email,
                phone: attendee.phone || phoneNumber,
              };
            }
            return attendee;
          });
          console.log('Auto-fill applied:', updatedAttendees[0]);
          return updatedAttendees;
        }
        console.log('Auto-fill skipped - fields already filled');
        return currentAttendees;
      });
    }
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="seated-registration-page">
        <Container>
          {/* Event Header Skeleton */}
          <div className="event-header-skeleton event-header">
            <div className="header-controls-skeleton mb-3">
              <SkeletonLoader type="rect" width="120px" height="36px" className="rounded" />
              <SkeletonLoader type="rect" width="200px" height="36px" className="rounded" />
            </div>
            <SkeletonLoader type="text" width="70%" height="32px" className="mx-auto mb-2" />
            <SkeletonLoader type="text" width="50%" height="20px" className="mx-auto mb-3" />
            <SkeletonLoader type="text" width="40%" height="16px" className="mx-auto" />
          </div>

          <Row className="content-wrapper">
            {/* Seat Selection Skeleton */}
            <Col lg={8}>
              <div className="seat-selection-skeleton seat-selection">
                {/* Legend Skeleton */}
                <div className="legend-skeleton legend mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="legend-item-skeleton">
                      <SkeletonLoader type="circle" width="18px" height="18px" />
                      <SkeletonLoader type="text" width="80px" height="16px" />
                    </div>
                  ))}
                </div>
                
                {/* Seat Stats Skeleton */}
                <div className="seat-stats-skeleton seat-stats mb-3">
                  <SkeletonLoader type="rect" width="120px" height="40px" className="rounded" />
                  <SkeletonLoader type="rect" width="120px" height="40px" className="rounded" />
                  <SkeletonLoader type="circle" width="36px" height="36px" />
                </div>
                
                {/* Seat Map Skeleton */}
                <SeatMapSkeleton />
              </div>
            </Col>

            {/* Form Section Skeleton */}
            <Col lg={4}>
              <div className="form-section-skeleton form-section">
                {/* Order Summary Skeleton */}
                <div className="subtotal-section-skeleton subtotal-section mb-4">
                  <SkeletonLoader type="text" width="150px" height="20px" className="mb-3" />
                  <SkeletonLoader type="text" width="100%" height="18px" className="mb-2" />
                  <SkeletonLoader type="text" width="80%" height="18px" className="mb-3" />
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <SkeletonLoader type="text" width="60px" height="20px" />
                    <SkeletonLoader type="text" width="80px" height="24px" />
                  </div>
                </div>

                {/* Form Header Skeleton */}
                <div className="form-header-skeleton mb-3">
                  <SkeletonLoader type="text" width="180px" height="20px" className="mb-2" />
                  <SkeletonLoader type="text" width="100%" height="16px" />
                </div>

                {/* Form Fields Skeleton */}
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="mb-3">
                    <SkeletonLoader type="text" width="80px" height="16px" className="mb-2" />
                    <SkeletonLoader type="rect" width="100%" height="40px" className="rounded" />
                  </div>
                ))}

                {/* Submit Button Skeleton */}
                <SkeletonLoader type="rect" width="100%" height="48px" className="rounded mt-4" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error && !eventData) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!eventData || !categoryData) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          <Alert.Heading>Not Found</Alert.Heading>
          Event or category not found.
        </Alert>
      </Container>
    );
  }

  return (
    <div className="seated-registration-page">
      <Container>
        {/* Header */}
        <div className="event-header">
          <div className="header-controls">
            <Button 
              variant="outline-secondary" 
              onClick={handleBackToEvent}
              className="back-button"
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Event
            </Button>
            
            {availableCategories.length > 1 && (
              <Dropdown className="category-dropdown">
                <Dropdown.Toggle variant="outline-primary" id="category-dropdown">
                  <i className="fas fa-ticket-alt me-2"></i>
                  {categoryData && categoryClusters.length > 0
                    ? (() => {
                        const cluster = categoryClusters.find(c => 
                          c.categories.some(cat => cat.id === categoryData.id)
                        );
                        return cluster 
                          ? `${cluster.clusterName} - ${categoryData.name}`
                          : categoryData.name;
                      })()
                    : categoryData?.name}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {categoryClusters.map((cluster) => (
                    <div key={cluster.clusterId}>
                      {categoryClusters.length > 1 && (
                        <Dropdown.Header className="cluster-header">
                          <i className="fas fa-layer-group me-2"></i>
                          {cluster.clusterName}
                        </Dropdown.Header>
                      )}
                      {cluster.categories.map((category) => (
                        <Dropdown.Item
                          key={category.id}
                          active={category.id === categoryId}
                          onClick={() => handleCategoryChange(category.id)}
                          className="category-item"
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="category-info">
                              <span className="category-name">
                                {cluster.clusterName} - {category.name}
                              </span>
                            </div>
                            <span className="category-price">
                              {eventData.setting.currency.symbol}{category.price}
                            </span>
                          </div>
                        </Dropdown.Item>
                      ))}
                      {categoryClusters.length > 1 && cluster !== categoryClusters[categoryClusters.length - 1] && (
                        <Dropdown.Divider />
                      )}
                    </div>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
          
          <h1>{eventData.title}</h1>
          <h3 className="text-center mb-0">
            {getCurrentClusterName() && (
              <span className="text-muted">{getCurrentClusterName()}: </span>
            )}
            {categoryData.name} - <span className="text-primary">
              {eventData.setting.currency.symbol}{categoryData.price}
            </span> <small className="text-muted">/seat</small>
          </h3>
          
          {/* Registration limits info */}
          <div className="registration-limits">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Max {Math.min(
                eventData.setting.registration_limit || 10,
                eventData.setting.quota_limit || 10,
                categoryData.available_seats || 10
              )} seats per registration
            </small>
          </div>
        </div>

        <Row className="content-wrapper">
          {/* Left Column - Seat Selection */}
          <Col lg={8}>
            <div className="seat-selection">
              {/* Seat Status Legend */}
              <div className="legend" role="group" aria-label="Seat status legend">
                <div className="legend-item">
                  <div className="legend-color bg-info" aria-hidden="true"></div>
                  <span>Available</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color bg-success" aria-hidden="true"></div>
                  <span>Selected</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color bg-danger" aria-hidden="true"></div>
                  <span>Reserved/Held/In Cart</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color bg-warning" aria-hidden="true"></div>
                  <span>Quota Reached</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color bg-muted" aria-hidden="true"></div>
                  <span>Hidden/Disabled</span>
                </div>
              </div>

              {/* Seat Stats */}
              <div className="seat-stats">
                <div className="stat-item">
                  <i className="fas fa-chair text-info"></i>
                  <span className="text-info fw-bold">
                    {seats.filter(seat => seat.status === 'available').length} Available
                  </span>
                </div>
                <div className="stat-item">
                  <i className="fas fa-check-circle text-success"></i>
                  <span className="text-success fw-bold">
                    {selectedSeats.size} Selected
                  </span>
                </div>
                <button 
                  className="refresh-seats-btn"
                  onClick={handleRefreshSeats}
                  title="Refresh seat availability"
                  disabled={loading}
                >
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                </button>
              </div>

              {/* Seat Map */}
              <SeatMap
                seats={seats}
                selectedSeats={selectedSeats}
                maxSeats={Math.min(
                  eventData.setting.registration_limit || 10,
                  eventData.setting.quota_limit || 10,
                  categoryData.available_seats || 10
                )}
                onSeatToggle={handleSeatToggle}
                pendingSeats={pendingSeats}
                quotaSeatId={quotaSeatId ?? undefined}
                conflictSeatId={conflictSeatId ?? undefined}
                loading={loading}
              />

              {/* Error Messages */}
              {error && (
                <Alert variant="danger" className="mt-3">
                  {error}
                </Alert>
              )}
            </div>
          </Col>

          {/* Right Column - Forms */}
          <Col lg={4}>
            <div className="form-section">
              {/* Subtotal Display */}
              <div className="subtotal-section">
                <div className="subtotal-header">
                  <h5>Order Summary</h5>
                </div>
                <div className="subtotal-details">
                  <div className="subtotal-item">
                    <span className="item-label">
                      {getCurrentClusterName() && (
                        <span className="cluster-name">{getCurrentClusterName()} - </span>
                      )}
                      {categoryData.name} × {selectedSeats.size}
                    </span>
                    <span className="item-price">
                      {eventData.setting.currency.symbol}{categoryData.price * selectedSeats.size}
                    </span>
                  </div>
                  <div className="subtotal-total">
                    <span className="total-label">Total</span>
                    <span className="total-price">
                      {eventData.setting.currency.symbol}{categoryData.price * selectedSeats.size}
                    </span>
                  </div>
                </div>
                {selectedSeats.size > 0 && (
                  <div className="selected-seats-list">
                    <small className="text-muted">Selected Seats:</small>
                    <div className="seat-numbers">
                      {Array.from(selectedSeats).map(seatId => {
                        const seat = seats.find(s => s.id === seatId);
                        return (
                          <span key={seatId} className="seat-number">
                            {seat?.seat_number}
                          </span>
                        );
                      })}
                    </div>
                    <div className="clear-all-section">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm clear-all-btn"
                        onClick={handleClearAllSeats}
                        disabled={loading}
                      >
                        <i className="fas fa-trash-alt me-1"></i>
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <Form onSubmit={handleSubmit}>
                <div className="form-header">
                  <h5>Registration Information</h5>
                  <p className="text-muted">
                    {eventData.setting.collect_individual_information 
                      ? 'Fill in details for each selected seat'
                      : 'Fill in your contact information'
                    }
                  </p>
                </div>

                {/* Primary Attendee */}
                <div className="form-group">
                  <label htmlFor="name">
                    {eventData.setting.collect_individual_information ? 'Primary Attendee Name' : 'Name'}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    required
                    placeholder="Pass Holder Name"
                    value={attendees[0]?.name || ''}
                    onChange={(e) => handleAttendeeChange(0, 'name', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    {eventData.setting.collect_individual_information ? 'Primary Attendee Email' : 'Email'}
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    required
                    placeholder="Email Address"
                    value={attendees[0]?.email || ''}
                    onChange={(e) => handleAttendeeChange(0, 'email', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <InternationalPhoneInput
                    value={attendees[0]?.phone || ''}
                    onChange={(value) => handleAttendeeChange(0, 'phone', value || '')}
                    placeholder="Phone Number"
                    id="phone"
                    name="phone"
                    label={eventData.setting.collect_individual_information ? 'Primary Attendee Phone' : 'Phone'}
                    showLabel={true}
                    required={true}
                  />
                </div>

                {/* Additional Attendees - Only show if individual info collection is enabled */}
                {selectedSeats.size > 1 && eventData.setting.collect_individual_information && (
                  <div className="additional-attendees">
                    <h5>Additional Attendees</h5>
                    {Array.from(selectedSeats).slice(1).map((seatId, index) => {
                      const seat = seats.find(s => s.id === seatId);
                      return (
                        <div key={seatId} className="attendee-card">
                          <h6>Attendee {index + 2} - Seat {seat?.seat_number}</h6>
                          <div className="form-group">
                            <label>Name</label>
                            <input
                              type="text"
                              className="form-control"
                              required
                              value={attendees[index + 1]?.name || ''}
                              onChange={(e) => handleAttendeeChange(index + 1, 'name', e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>Email</label>
                            <input
                              type="email"
                              className="form-control"
                              required
                              value={attendees[index + 1]?.email || ''}
                              onChange={(e) => handleAttendeeChange(index + 1, 'email', e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <InternationalPhoneInput
                              value={attendees[index + 1]?.phone || ''}
                              onChange={(value) => handleAttendeeChange(index + 1, 'phone', value || '')}
                              placeholder="Phone Number"
                              id={`phone_${index + 2}`}
                              name={`phone_${index + 2}`}
                              label="Phone"
                              showLabel={true}
                              required={true}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="submit-btn"
                  disabled={selectedSeats.size === 0 || submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    'Proceed to Cart'
                  )}
                </Button>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SeatedRegistrationPage;
